import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardPageHeader from "../../components/Dashboard/DashboardPageHeader";
import NewOrderModal from "../../components/NewOrderModal";
import {
  getDateRanges,
  getShowingText,
  translateOrderStatus,
} from "../../i18n/dashboardHelpers";
import {
  approveSimRequest,
  getProviderSimRequestStats,
  getProviderSimRequests,
  getSimRequestStats,
  getSimRequests,
  rejectSimRequest,
} from "../../services/apiService";
import { isPartner } from "../../utils/auth";

const PAGE_SIZE = 20;

function getStatusFilterOptions(t) {
  return [
    { value: "all", label: t("dashboard.common.allStatus") },
    { value: "approved", label: t("dashboard.status.completed") },
    { value: "pending", label: t("dashboard.status.pending") },
    { value: "rejected", label: t("dashboard.status.canceled") },
  ];
}

const statusStyles = {
  PENDING: "dashboard-badge--pending",
  COMPLETED: "dashboard-badge--completed",
  CANCELED: "dashboard-badge--canceled",
};

const STAT_ICONS = {
  total: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6h15l-1.5 9H7.5L6 6zM6 6L5 3H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="20" r="1" fill="currentColor" />
      <circle cx="18" cy="20" r="1" fill="currentColor" />
    </svg>
  ),
  pending: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="18" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  approved: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  rejected: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

function getDateRange(days) {
  const end = new Date();
  end.setUTCHours(23, 59, 59, 999);

  const start = new Date();
  start.setUTCDate(start.getUTCDate() - days + 1);
  start.setUTCHours(0, 0, 0, 0);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

function extractItems(response) {
  if (Array.isArray(response?.data)) return response.data;
  return response?.data?.data ?? [];
}

function extractTotal(response, fallback = 0) {
  return (
    response?.pagination?.total ??
    response?.pagination?.totalItems ??
    response?.data?.total ??
    response?.data?.meta?.total ??
    fallback
  );
}

function extractTotalPages(response, totalItems = 0) {
  if (response?.pagination?.totalPages != null) {
    return response.pagination.totalPages;
  }
  return totalItems === 0 ? 0 : Math.ceil(totalItems / PAGE_SIZE);
}

function getPaginationPages(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "ellipsis", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function extractStatValue(stats, key) {
  const value = stats?.[key];
  if (value == null) return 0;
  if (typeof value === "object") return value.count ?? value.value ?? 0;
  return value;
}

function extractStatRate(stats, key) {
  return stats?.rate?.[key] ?? null;
}

function formatRate(rate) {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }

  const numericRate = Number(rate);
  const sign = numericRate >= 0 ? "+" : "";
  return `${sign}${numericRate.toFixed(1)}%`;
}

function formatDate(value, locale) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRequestId(id) {
  if (!id) return "—";
  return id;
}

function TrendBadge({ value }) {
  if (!value) return null;

  const isPositive = !value.startsWith("-");
  return (
    <span className={`dashboard-trend${isPositive ? "" : " dashboard-trend--down"}`}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {isPositive ? (
          <path
            d="M7 14l4-4 3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7 10l4 4 3-3 5 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      {value}
    </span>
  );
}

function StatCard({ label, value, change, icon, iconClass }) {
  return (
    <div className="dashboard-stat-card dashboard-stat-card--with-icon">
      <p className="dashboard-stat-card__label">{label}</p>
      <div className="dashboard-stat-card__row">
        <div className="dashboard-stat-card__content">
          <p className="dashboard-stat-card__value">{value}</p>
          {change && <TrendBadge value={change} />}
        </div>
        <span className={`dashboard-stat-card__icon ${iconClass}`}>{icon}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const label = translateOrderStatus(t, status);
  const normalized = String(status || "").toLowerCase();
  const styleKey = {
    pending: "PENDING",
    approved: "COMPLETED",
    rejected: "CANCELED",
  }[normalized];
  return (
    <span className={`dashboard-badge ${statusStyles[styleKey] || ""}`}>
      {label}
    </span>
  );
}

function ApproveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RejectIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function isPendingStatus(status) {
  return status?.toLowerCase() === "pending";
}

function getProviderLabel(order) {
  return order.provider?.fullName || order.provider?.email || "—";
}

function OrderActionConfirmModal({ confirmAction, onClose, onConfirm, t }) {
  const [rejectNote, setRejectNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!confirmAction) {
      setRejectNote("");
      setIsSubmitting(false);
      setError("");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [confirmAction, isSubmitting, onClose]);

  if (!confirmAction) return null;

  const { order, action } = confirmAction;
  const isApprove = action === "approve";
  const providerLabel = getProviderLabel(order);
  const title = isApprove
    ? t("dashboard.orders.approveTitle")
    : t("dashboard.orders.rejectTitle");
  const message = isApprove
    ? t("dashboard.orders.approveMessage", {
        provider: providerLabel,
        quantity: order.quantity?.toLocaleString("en-US") ?? 0,
      })
    : t("dashboard.orders.rejectMessage", { provider: providerLabel });

  const handleConfirm = async () => {
    if (!isApprove && !rejectNote.trim()) {
      setError(t("dashboard.orders.rejectionRequired"));
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      if (isApprove) {
        await approveSimRequest(order.id);
      } else {
        await rejectSimRequest(order.id, { note: rejectNote.trim() });
      }
      onConfirm?.();
      onClose();
    } catch (fetchError) {
      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          t("dashboard.orders.actionFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-action-confirm-title"
      >
        <div className="settings-modal__header">
          <h2 id="order-action-confirm-title" className="settings-modal__title">
            {title}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">{message}</p>

        {!isApprove ? (
          <div className="settings-field">
            <label htmlFor="order-reject-note">{t("dashboard.orders.rejectionReason")}</label>
            <textarea
              id="order-reject-note"
              value={rejectNote}
              onChange={(event) => setRejectNote(event.target.value)}
              placeholder={t("dashboard.orders.rejectionPlaceholder")}
              disabled={isSubmitting}
            />
          </div>
        ) : null}

        {error ? (
          <p className="settings-form-status settings-form-status--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="settings-modal__actions">
          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dashboard.common.cancel")}
          </button>
          <button
            type="button"
            className={`settings-btn ${isApprove ? "settings-btn--primary" : "settings-btn--danger"}`}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("dashboard.common.processing")
              : isApprove
                ? t("dashboard.common.approve")
                : t("dashboard.common.reject")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const { t, i18n } = useTranslation();
  const partnerView = isPartner();
  const dateRanges = useMemo(() => getDateRanges(t), [t]);
  const statusFilterOptions = useMemo(() => getStatusFilterOptions(t), [t]);
  const [dateRange, setDateRange] = useState("7");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionConfirm, setActionConfirm] = useState(null);

  const adminColumnCount = 7;
  const partnerColumnCount = 5;

  const selectedRange = dateRanges.find((range) => range.value === dateRange) ?? dateRanges[0];
  const { startDate, endDate } = useMemo(
    () => getDateRange(selectedRange.days),
    [selectedRange.days]
  );

  const activeStatus = statusFilter === "all" ? undefined : statusFilter;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = partnerView
        ? await getProviderSimRequestStats({ startDate, endDate })
        : await getSimRequestStats({ startDate, endDate });
      setStats(response?.data ?? response ?? null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [partnerView, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      setLoading(true);
      setError("");

      try {
        const listParams = {
          status: activeStatus,
          search: debouncedSearch || undefined,
          startDate,
          endDate,
          page,
          pageSize: PAGE_SIZE,
        };
        const response = partnerView
          ? await getProviderSimRequests(listParams)
          : await getSimRequests(listParams);

        if (cancelled) return;

        const items = extractItems(response);
        const total = extractTotal(response, items.length);
        setOrders(items);
        setTotalItems(total);
        setTotalPages(extractTotalPages(response, total));
      } catch (fetchError) {
        if (cancelled) return;

        setOrders([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(fetchError?.response?.data?.message || t("dashboard.orders.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [partnerView, activeStatus, debouncedSearch, startDate, endDate, page, refreshKey, t]);

  const statCards = useMemo(
    () => [
      {
        label: t("dashboard.orders.totalOrders"),
        value: statsLoading ? "—" : extractStatValue(stats, "total").toLocaleString("en-US"),
        change: formatRate(extractStatRate(stats, "total")),
        icon: STAT_ICONS.total,
        iconClass: "dashboard-stat-card__icon--blue",
      },
      {
        label: t("dashboard.status.pending"),
        value: statsLoading ? "—" : extractStatValue(stats, "pending").toLocaleString("en-US"),
        change: formatRate(extractStatRate(stats, "pending")),
        icon: STAT_ICONS.pending,
        iconClass: "dashboard-stat-card__icon--yellow",
      },
      {
        label: t("dashboard.status.completed"),
        value: statsLoading ? "—" : extractStatValue(stats, "approved").toLocaleString("en-US"),
        change: formatRate(extractStatRate(stats, "approved")),
        icon: STAT_ICONS.approved,
        iconClass: "dashboard-stat-card__icon--green",
      },
      {
        label: t("dashboard.status.canceled"),
        value: statsLoading ? "—" : extractStatValue(stats, "rejected").toLocaleString("en-US"),
        change: formatRate(extractStatRate(stats, "rejected")),
        icon: STAT_ICONS.rejected,
        iconClass: "dashboard-stat-card__icon--red",
      },
    ],
    [stats, statsLoading, t]
  );

  const paginationPages = getPaginationPages(page, totalPages);
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);
  const tableColumnCount = partnerView ? partnerColumnCount : adminColumnCount;

  const handleOrderActionUpdated = () => {
    setRefreshKey((current) => current + 1);
    fetchStats();
  };

  const openActionConfirm = (order, action) => {
    setActionConfirm({ order, action });
  };

  return (
    <div className="dashboard-page">
      <DashboardPageHeader title={t("dashboard.orders.title")}>
        <select
          className="dashboard-select"
          value={dateRange}
          onChange={(event) => {
            setDateRange(event.target.value);
            setPage(1);
          }}
          aria-label={t("dashboard.common.dateRange")}
        >
          {dateRanges.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
        <button type="button" className="dashboard-btn-outline">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3v12M8 11l4 4 4-4M4 21h16"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("dashboard.common.export")}
        </button>
        {partnerView && (
          <button
            type="button"
            className="dashboard-btn-primary"
            onClick={() => setIsNewOrderModalOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t("dashboard.orders.newOrder")}
          </button>
        )}
      </DashboardPageHeader>

      <div className="dashboard-stats">
        {statCards.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="dashboard-card dashboard-card--table">
        <div className="dashboard-table-toolbar dashboard-table-toolbar--orders">
          <div className="dashboard-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
              <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              className="dashboard-search__input"
              placeholder={t("dashboard.orders.searchPlaceholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t("dashboard.orders.searchAria")}
            />
          </div>
          <div className="dashboard-table-toolbar__actions">
            <select
              className="dashboard-select"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              aria-label={t("dashboard.common.filterByStatus")}
            >
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>{t("dashboard.common.orderId")}</th>
                {!partnerView && <th>{t("dashboard.common.provider")}</th>}
                <th>{t("dashboard.common.quantity")}</th>
                {!partnerView && <th>{t("dashboard.customers.esims")}</th>}
                {partnerView && <th>{t("dashboard.common.note")}</th>}
                <th>{t("dashboard.common.date")}</th>
                <th>{t("dashboard.common.status")}</th>
                {!partnerView && <th>{t("dashboard.common.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColumnCount}>{t("dashboard.orders.loading")}</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={tableColumnCount}>{error}</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount}>{t("dashboard.orders.noOrders")}</td>
                </tr>
              ) : (
                orders.map((order) => {
                  const providerName = getProviderLabel(order);
                  return (
                    <tr key={order.id}>
                      <td>
                        <button type="button" className="dashboard-table__order-id">
                          #{formatRequestId(order.id)}
                        </button>
                      </td>
                      {!partnerView && (
                        <td className="dashboard-table__name">{providerName}</td>
                      )}
                      <td>{order.quantity?.toLocaleString(i18n.language) ?? "—"}</td>
                      {!partnerView && (
                        <td className="dashboard-table__amount">
                          {order._count?.esims?.toLocaleString(i18n.language) ?? "0"}
                        </td>
                      )}
                      {partnerView && <td>{order.note || "—"}</td>}
                      <td className="dashboard-table__date">
                        {formatDate(order.created_at || order.createdAt, i18n.language)}
                      </td>
                      <td>
                        <StatusBadge status={order.status} t={t} />
                      </td>
                      {!partnerView && (
                        <td>
                          {isPendingStatus(order.status) ? (
                            <div className="dashboard-table__actions">
                              <button
                                type="button"
                                className="dashboard-table__actions-btn dashboard-table__actions-btn--approve"
                                aria-label={t("dashboard.orders.approveOrder", { id: order.id })}
                                onClick={() => openActionConfirm(order, "approve")}
                              >
                                <ApproveIcon />
                              </button>
                              <button
                                type="button"
                                className="dashboard-table__actions-btn dashboard-table__actions-btn--reject"
                                aria-label={t("dashboard.orders.rejectOrder", { id: order.id })}
                                onClick={() => openActionConfirm(order, "reject")}
                              >
                                <RejectIcon />
                              </button>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-table-footer">
          <p className="dashboard-table-footer__info">
            {getShowingText(t, {
              entity: "orders",
              start: rangeStart,
              end: rangeEnd,
              total: totalItems,
            })}
          </p>
          <nav className="dashboard-pagination" aria-label={t("dashboard.orders.paginationAria")}>
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.previousPage")}
              disabled={page <= 1 || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {paginationPages.map((pageNumber, index) =>
              pageNumber === "ellipsis" ? (
                <span key={`ellipsis-${index}`} className="dashboard-pagination__ellipsis" aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={`dashboard-pagination__page${
                    page === pageNumber ? " dashboard-pagination__page--active" : ""
                  }`}
                  aria-current={page === pageNumber ? "page" : undefined}
                  disabled={loading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              type="button"
              className="dashboard-pagination__btn"
              aria-label={t("dashboard.common.nextPage")}
              disabled={page >= totalPages || loading || totalPages === 0}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </nav>
        </div>
      </div>

      {partnerView && (
        <NewOrderModal
          isOpen={isNewOrderModalOpen}
          onClose={() => setIsNewOrderModalOpen(false)}
          onSuccess={() => {
            setPage(1);
            setRefreshKey((current) => current + 1);
            fetchStats();
          }}
        />
      )}

      {!partnerView && (
        <OrderActionConfirmModal
          confirmAction={actionConfirm}
          onClose={() => setActionConfirm(null)}
          onConfirm={handleOrderActionUpdated}
          t={t}
        />
      )}
    </div>
  );
}

export default Orders;
